import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { QuizProvider } from "./context/QuizContext";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import BottomNavigation from "./components/BottomNavigation";
import { useEffect } from "react";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import QuizPage from "./pages/QuizPage";
import EditAnswersPage from "./pages/EditAnswersPage";
import ResultsPage from "./pages/ResultsPage";
import DashboardPage from "./pages/DashboardPage";
import ProgressPage from "./pages/ProgressPage";
import SupplementsPage from "./pages/SupplementsPage";
import SharedResultsPage from "./pages/SharedResultsPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import RemindersPage from "./pages/RemindersPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SymptomTrackingPage from "./pages/SymptomTrackingPage";
import AssessmentComparisonPage from "./pages/AssessmentComparisonPage";
import ProviderPortalPage from "./pages/ProviderPortalPage";
import SupplementJournalPage from "./pages/SupplementJournalPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import AdminPage from "./pages/AdminPage";
import ForgotAdminSecretPage from "./pages/ForgotAdminSecretPage";
import ResetAdminSecretPage from "./pages/ResetAdminSecretPage";
import GamificationPage from "./pages/GamificationPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import ProfilePage from "./pages/ProfilePage";
import TelehealthPage from "./pages/TelehealthPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import CommunityPage from "./pages/CommunityPage";
import AskAIPage from "./pages/AskAIPage";
import GuidePage from "./pages/GuidePage";
import ScrollToTop from "./components/ScrollToTop";
import ScannerPage from "./pages/ScannerPage";
import MSGuidePage from "./pages/MSGuidePage";
import ThyroidGuidePage from "./pages/ThyroidGuidePage";
import PCOSGuidePage from "./pages/PCOSGuidePage";
import ArthritisGuidePage from "./pages/ArthritisGuidePage";
import DiabetesGuidePage from "./pages/DiabetesGuidePage";
import IBSGuidePage from "./pages/IBSGuidePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import MyStackPage from "./pages/MyStackPage";
import MarketingPage from "./pages/MarketingPage";
import MyPlanPage from "./pages/MyPlanPage";
import WhatChangedPage from "./pages/WhatChangedPage";
import FeedbackPage from "./pages/FeedbackPage";
import CalculatorPage from "./pages/CalculatorPage";

// Register service worker early
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('[App] Service Worker registered:', registration.scope);

        // Tell the SW we're running in standalone PWA mode (not a browser tab).
        // The SW uses this to route notification clicks ONLY into the installed PWA,
        // never into an open Chrome browser tab.
        const isStandalone =
          window.matchMedia('(display-mode: standalone)').matches ||
          window.matchMedia('(display-mode: minimal-ui)').matches ||
          window.matchMedia('(display-mode: fullscreen)').matches ||
          window.navigator.standalone === true; // iOS PWA

        const notifyStandalone = () => {
          if (!isStandalone) return;
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'PWA_STANDALONE_READY' });
            console.log('[App] Registered as PWA standalone client');
          }
        };
        // Try immediately, on controllerchange, and on a short delay loop
        // (covers first install where `controller` is initially null).
        notifyStandalone();
        navigator.serviceWorker.addEventListener('controllerchange', notifyStandalone);
        if (isStandalone) {
          // Re-ping until controller appears (max 5 attempts over 5s)
          let attempts = 0;
          const handshake = setInterval(() => {
            attempts += 1;
            if (navigator.serviceWorker.controller) {
              notifyStandalone();
              clearInterval(handshake);
            } else if (attempts >= 10) {
              clearInterval(handshake);
            }
          }, 500);
        }
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[App] New service worker available');
            }
          });
        });
      } catch (error) {
        console.error('[App] Service Worker registration failed:', error);
      }
    });
  }
}

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <AuthProvider>
      <QuizProvider>
        <BrowserRouter>
          <ScrollToTop />
          <OfflineIndicator />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/edit-answers" element={<EditAnswersPage />} />
            <Route path="/results/:id" element={<ResultsPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/supplements" element={<SupplementsPage />} />
            <Route path="/shared/:token" element={<SharedResultsPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/calculator" element={<CalculatorPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/symptoms" element={<SymptomTrackingPage />} />
            <Route path="/compare" element={<AssessmentComparisonPage />} />
            <Route path="/provider" element={<ProviderPortalPage />} />
            <Route path="/journal" element={<SupplementJournalPage />} />
            <Route path="/analytics" element={<AnalyticsDashboardPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/forgot-secret" element={<ForgotAdminSecretPage />} />
            <Route path="/admin/reset-secret" element={<ResetAdminSecretPage />} />
            <Route path="/rewards" element={<GamificationPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/telehealth" element={<TelehealthPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/blog" element={<CommunityPage />} />
            <Route path="/ask" element={<AskAIPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/lookup" element={<ScannerPage />} />
            <Route path="/scanner" element={<ScannerPage />} />
            <Route path="/ms-guide" element={<MSGuidePage />} />
            <Route path="/thyroid-guide" element={<ThyroidGuidePage />} />
            <Route path="/pcos-guide" element={<PCOSGuidePage />} />
            <Route path="/arthritis-guide" element={<ArthritisGuidePage />} />
            <Route path="/diabetes-guide" element={<DiabetesGuidePage />} />
            <Route path="/ibs-guide" element={<IBSGuidePage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionPage />} />
            <Route path="/my-stack" element={<MyStackPage />} />
            <Route path="/app" element={<MarketingPage />} />
            <Route path="/my-plan" element={<MyPlanPage />} />
            <Route path="/what-changed/:id" element={<WhatChangedPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
          </Routes>
          <BottomNavigation />
          <PWAInstallPrompt />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </QuizProvider>
    </AuthProvider>
  );
}

export default App;
